import { useEffect, useState } from 'react';

import { ApiError } from '@/components/modules/_shared/exceptions/ApiError';
import { useCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useLoanPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useLoanPaymentIntent';
import { useGetOnboardingDetailsQuery } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/hooks/useGetOnboardingDetailsQuery';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';

interface MultilenderProps {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  onOpenMerchantPos: () => void;
}

export const Multilender = ({ paymentMethodDetailEntity, onOpenMerchantPos }: MultilenderProps) => {
  const { checkoutSessionEntity } = useCheckoutSession();
  const { createPaymentIntent, mountPaymentIntent } = useLoanPaymentIntent();
  const getOnboardingDetailsQuery = useGetOnboardingDetailsQuery(paymentMethodDetailEntity);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hideButton, setHideButton] = useState(false);

  useEffect(() => {
    getOnboardingDetailsQuery.queryAsync();
  }, []);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  if (!checkoutSessionEntity || checkoutSessionEntity.hasFinalStatus()) {
    return null;
  }

  const onStart = async () => {
    setLoading(true);
    try {
      await createPaymentIntent(paymentMethodDetailEntity);
      setHideButton(true);
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error);
        setLoading(false);
        return;
      }
    }
    await mountPaymentIntent(paymentMethodDetailEntity, onOpenMerchantPos);
    setLoading(false);
  };

  const shouldCreatePaymentIntent = paymentMethodDetailEntity.shouldCreatePaymentIntent();

  const isExpired = paymentMethodDetailEntity.isExpired();

  if (hideButton) {
    return null;
  }

  return (
    <StandardButton
      main
      size="small"
      loading={loading}
      onClick={onStart}
      disabled={getOnboardingDetailsQuery.isLoading}
    >
      <Text tId={shouldCreatePaymentIntent ? (isExpired ? 'RESTART' : 'START') : 'CONTINUE_APPLICATION'} />
    </StandardButton>
  );
};
